import { cn } from '@resolute/ui';
import { ImageOff } from 'lucide-react';
import { useState, type ImgHTMLAttributes } from 'react';


import { buildSrcSet, lqip } from '@/lib/image';

/** Transient network hiccups usually succeed on a quick retry. */
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 700;

export interface ProgressiveImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  /** Low-quality placeholder shown (blurred) until the full image loads. When
   *  omitted, a blurred LQIP is derived from `src` for supported providers. */
  blurDataUrl?: string;
  /** Tailwind aspect-ratio class — reserves space to avoid layout shift (CLS). */
  aspect?: string;
  /** Responsive `sizes` hint (e.g. "(min-width:1024px) 360px, 100vw"). */
  sizes?: string;
  /** Above-the-fold image: load eagerly with high fetch priority (no lazy). */
  priority?: boolean;
  /** Force eager loading (bypass native lazy-load) WITHOUT elevating fetch
   *  priority. For off-screen carousel/gallery slides that must be fetched
   *  ahead of time (native lazy-loading can't reliably detect slides revealed
   *  by a CSS transform), but that shouldn't compete for bandwidth with the
   *  slide the user is actually looking at — see `priority`. Redundant when
   *  `priority` is already true. */
  eager?: boolean;
  /** Override the candidate widths used to build the srcSet. */
  widths?: number[];
  /** Slow zoom-in on hover of the nearest `.group` ancestor (e.g. a card). */
  zoom?: boolean;
}

/**
 * Responsive, optimized image with a blur-up placeholder.
 *
 * - Reserves space via an aspect-ratio box → zero cumulative layout shift.
 * - Emits a responsive `srcSet` so the browser fetches the right resolution
 *   (Retina-aware) and modern formats (AVIF/WebP) where supported.
 * - Native lazy-loading for below-the-fold images; `priority` opts the hero /
 *   first paint into eager, high-priority loading. A carousel/gallery whose
 *   slides are revealed via CSS transform (not scroll) needs every slide to
 *   load eagerly — native lazy-loading doesn't reliably re-evaluate images
 *   shifted into view by a transform, so non-eager slides can end up
 *   permanently stuck unloaded. Pass `priority` on only the currently-visible
 *   slide and `eager` on the rest: all slides still fetch ahead of time, but
 *   only the one the user is actually looking at competes for bandwidth at
 *   high priority — forcing every slide of every card in a grid to `priority`
 *   causes dozens of simultaneous high-priority requests that can stall or
 *   intermittently fail against a third-party CDN.
 * - On a failed load, retries a couple of times (transient network hiccups
 *   usually succeed on retry) before falling back to a neutral placeholder,
 *   rather than leaving the image invisible forever with no recovery.
 */
export function ProgressiveImage({
  src,
  alt,
  blurDataUrl,
  aspect = 'aspect-[4/3]',
  sizes = '100vw',
  priority = false,
  eager = false,
  widths,
  zoom = false,
  className,
  ...props
}: ProgressiveImageProps) {
  const [loaded, setLoaded] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);
  const srcSet = buildSrcSet(src, widths);
  const placeholder = blurDataUrl ?? lqip(src);

  const handleError = () => {
    if (attempt < MAX_RETRIES) {
      // Remount (via the `key` below) rather than just reassigning `src`, so
      // the browser issues a genuinely fresh request instead of a no-op for
      // an unchanged attribute.
      window.setTimeout(() => setAttempt((a) => a + 1), RETRY_DELAY_MS * (attempt + 1));
    } else {
      setFailed(true);
    }
  };

  if (failed) {
    return (
      <div
        className={cn(
          'relative flex items-center justify-center overflow-hidden bg-muted text-muted-foreground',
          aspect,
          className,
        )}
      >
        <ImageOff className="size-6" aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className={cn('relative overflow-hidden bg-muted', aspect, className)}>
      {placeholder && !loaded && (
        <img
          src={placeholder}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 size-full scale-105 object-cover blur-xl"
        />
      )}
      <img
        key={attempt}
        src={src}
        srcSet={srcSet}
        sizes={srcSet ? sizes : undefined}
        alt={alt}
        loading={priority || eager ? 'eager' : 'lazy'}
        fetchPriority={priority ? 'high' : 'auto'}
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={handleError}
        className={cn(
          'size-full object-cover transition-[opacity,transform] duration-slow ease-out-expo',
          loaded ? 'opacity-100' : 'opacity-0',
          zoom && 'will-change-transform group-hover:scale-[1.06]',
        )}
        {...props}
      />
    </div>
  );
}
